import ContentStackFile from '../interfaces/ContentStackFile'
import ContentStackLink from '../interfaces/ContentStackLink'
import styled, { css } from 'styled-components'
import { breakpoint } from '../layout/styles'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

interface PopupProps {
  data: {
    title: string;
    description: string;
    main_image?: ContentStackFile|null;
    logo_image_1?: ContentStackFile|null;
    logo_1_max_height?: number;
    logo_image_2?: ContentStackFile|null;
    logo_2_max_height?: number;
    cta: ContentStackLink|null;
    cta_new_tab?: boolean;
    background_color?: string;
    text_color?: string;
    uid?: string;
    // some old fields (images, etc.) from version 1 may be passed
    [key: string]: any;
  }
}

const PopUp = ({ data }: PopupProps ) => {

  const { title, description, main_image, logo_image_1,
    logo_image_2, cta, cta_new_tab, uid } = data;

  let { background_color, text_color, logo_1_max_height, logo_2_max_height } = data;

  background_color = background_color || '#0D5D87';
  text_color = text_color || '#FFFFFF';
  logo_1_max_height = logo_1_max_height || 200;
  logo_2_max_height = logo_2_max_height || 87;

  const [open, setOpen] = useState(true);

  useEffect(() => {
    try {
      const pop_up_status = sessionStorage.getItem(`pop_up_${uid}_status`);
      if(pop_up_status === 'closed') {
        setOpen(false);
      }
    } catch {
      // sessionStorage blocked (cross-origin iframe, privacy mode, etc.)
    }
  }, [])

  // disable page scrollbar when popup is open.
  // The cleanup function ensures the class is always removed when the
  // component unmounts (e.g. parent navigates away while popup is open),
  // preventing a permanent scroll lock on the page.
  useEffect(() => {
    if ( open ) {
      document.documentElement.classList.add('popup-open');
    } else {
      document.documentElement.classList.remove('popup-open');
    }
    return () => {
      document.documentElement.classList.remove('popup-open');
    };
  }, [ open ]);

  const handleClick = ((e: any) => {
    e.preventDefault();
    if ( ! e.target.closest('.popup-inner') ) {
      setOpen(false);
      setStorage();
    }
  });

  const setStorage = (() => {
    try {
      sessionStorage.setItem(`pop_up_${uid}_status`, 'closed');
    } catch {
      // sessionStorage blocked (cross-origin iframe, privacy mode, etc.)
    }
  })

  // close on esc
  useEffect(() => {
    const close = (e:any) => {
      if(e.keyCode === 27){
        setOpen(false)
        setStorage();
      }
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  },[])

  if(!open)
    return null;
  return  (
    <Root key={uid} onClick={handleClick}>
      <Inner className="popup-inner" background_color={background_color}>
        <MainImage>
          {main_image?.url && (
            <img src={`${main_image.url}?width=900&auto=webp&format=pjpg&quality=75`} alt={main_image.title} />
          )}
        </MainImage>
        <Content>
          <ContentInner>
            {logo_image_1 && (
              <LogoImage1 max_height={logo_1_max_height}>
                <img src={`${logo_image_1?.url}?width=300&auto=webp&format=pjpg&quality=75`} alt={logo_image_1?.title} />
              </LogoImage1>
            )}
            <ContentInner2 text_color={text_color}>
              {logo_image_2 && (
                <LogoImage2 max_height={logo_2_max_height}>
                  <img src={`${logo_image_2?.url}?width=400&auto=webp&format=pjpg&quality=75`} alt={logo_image_2?.title} />
                </LogoImage2>
              )}
              {title && (
                <h2 dangerouslySetInnerHTML={{__html: title}}></h2>
              )}
              {description && (
                <p dangerouslySetInnerHTML={{ __html: description }}></p>
              )}
              {cta?.title && (
                <CtaWrapper>
                  <CTA
                    onClick={(el) => {
                      if(cta_new_tab) {
                        window.open(cta.href)
                        return;
                      }
                      window.location.href=cta.href;
                    }}
                    text_color={text_color}
                    background_color={background_color}
                    href={cta.href}
                    title={cta.title}
                    target={cta_new_tab ? '_blank' : '_self'}>
                    {cta.title}
                  </CTA>
                </CtaWrapper>
              )}
            </ContentInner2>
          </ContentInner>
          <CloseButton
            text_color={text_color}
            background_color={background_color}
            href={'#'}
            title="Close Popup"
            onClick={(e) => { e.preventDefault(); setOpen(false); setStorage(); }}>
            <span>&times;</span>
          </CloseButton>
        </Content>
      </Inner>
    </Root>
  )
}

const desktopSpacingBreakpoint = '(min-width: 1300px)';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: center;
  background-color: rgba(0,0,0,0.75);
  padding: 2rem 0;
  @media screen and ${desktopSpacingBreakpoint}{
    padding: 3.25rem 0;
  }
`;

// use top and bottom auto margins instead of justify-content: center
// on parent container. The auto vertical margins behave differently
// with regards to the vertical scrollbar.
const Inner = styled.dialog<{ background_color : string }>`
  position: relative;
  z-index: 20;
  margin: auto;
  width: 92%;
  max-width: 36rem;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  border: none;
  box-sizing: border-box;
  padding: 0;
  background-color: ${props => props.background_color};
  @media screen and ${breakpoint.tablet}{
    max-width: 79rem;
  }
`;

const MainImage = styled.div`
  position: relative;
  display: none;
  @media screen and ${breakpoint.tablet}{
    display: block;
    width: 43%;
  }
  img {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
`

const Content = styled.div`
  position: relative;
  width: 100%;
  padding: 0 3.75rem 0 2.5rem;
  @media screen and ${breakpoint.tablet}{
    width: 57%;
    padding: 0 4rem 0 5.45%;
  }
`

const ContentInner = styled.div``

const LogoImage1 = styled.div<{ max_height : number }>`
  img{
    display: block;
    max-width: 100%;
    ${props => props.max_height && css`
      max-height: ${props.max_height * 0.75}px;
      @media screen and ${breakpoint.tablet}{
        max-height: ${props.max_height * 0.9}px;
      }
      @media screen and ${desktopSpacingBreakpoint}{
        max-height: ${props.max_height}px;
      }
    `}
    width: auto;
    height: auto;
    // not sure if this does much here (maybe useful
    // if the image is really wide on mobile)
    object-fit: contain;
  }
`;

const LogoImage2 = styled.div<{ max_height : number }>`
  padding-bottom: 0.375rem;
  img{
    display: block;
    max-width: 100%;
    ${props => props.max_height && css`
      max-height: ${props.max_height * 0.8}px;
      @media screen and ${breakpoint.desktop} {
        max-height: ${props.max_height}px;
      }
    `}
    width: auto;
    height: auto;
    // not sure if this does much here (maybe useful
    // if the image is too wide on mobile)
    object-fit: contain;
  }
`;

const ContentInner2 = styled.div<{ text_color : string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.25rem;
  padding: 3rem 0 3.5rem 0;
  @media screen and ${breakpoint.phablet}{
    min-height: 27rem;
  }
  @media screen and ${desktopSpacingBreakpoint}{
    min-height: 32rem;
  }
  h2 {
    color: ${props => props.text_color};
    font-weight: 400;
    font-size: 1.5rem;
    line-height: 1.15em;
    margin: 0;
    @media screen and ${breakpoint.desktop} {
      font-size: 2rem;
    }
  }
  p {
    color: ${props => props.text_color};
    font-weight: 300;
    font-size: 1rem;
    line-height: 1.25em;
    margin: 0;
    width: 100%;
    max-width: 30rem;
    @media screen and ${breakpoint.desktop} {
      font-size: 1.125rem;
    }
  }
`

const CloseButton = styled.a<{ background_color : string, text_color : string }>`
  position: absolute;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  top: 1rem;
  right: 1rem;
  text-decoration: none;
  background-color: ${props => props.text_color};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 2px solid ${props => props.text_color};
  transition: background-color 0.3s ease;
  outline: none;
  span{
    font-size: 1.75rem;
    line-height: 1em;
    color: ${props => props.background_color};
    transition: color 0.3s ease;
    @media screen and ${breakpoint.mobile} {
      margin-top: 0.3125rem;
    }
    @media screen and ${breakpoint.desktop} {
      margin-top: 0.189rem;
    }
  }
  &:hover, &:focus-visible{
    background-color: transparent;
    span{
      color: ${props => props.text_color};
    }
  }
`

const CtaWrapper = styled.div`
  padding-top: 0.5rem;
  @media screen and ${breakpoint.tablet}{
    padding-top: 0.75rem;
  }
`

const CTA = styled.a<{ background_color : string, text_color : string }>`
  display: inline-block;
  font-size: 1rem;
  line-height: 1.1em;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1.125rem;
  min-width: 12rem;
  text-align: center;
  border: none;
  background-color: ${props => props.text_color};
  color: ${props => props.background_color};
  text-decoration: none;
  outline: none;
  @media screen and ${breakpoint.tablet} {
    min-width: 18.25rem;
    padding: 1.25rem;
    font-size: 1.0625rem;
  }
  &:hover, &:focus-visible{
    text-decoration: underline;
  }
`

export default PopUp;